import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Check, Loader2, Zap, ShieldCheck, Info, Crown, Lock } from "lucide-react";
import LaunchPromoBanner from "@/components/LaunchPromoBanner";
import CertHolderBanner from "@/components/CertHolderBanner";

// Per-language Pricing copy. Stripe-related legal text remains identical
// (Slovak company SauberEko s.r.o.) since it's a legal disclosure.
const TR = {
  sk: {
    twoDays: "2 DNI", perTwoDays: "/ 2 dni",
    autoRenew: "AUTO-OBNOVA", oneTime: "JEDNORAZOVO",
    paid: "Aktívne predplatné", currentPlan: "Tvoj aktuálny plán", upgradeTo: "Upgrade na",
    downgradeNote: "Tvoj aktuálny plán je vyšší", mostPopular: "Najobľúbenejšie",
    actSubscribe: (period) => `predplať si ${period}`, // recurring
    actActivate: (period) => `aktivuj na ${period}`,   // one-time
    regAnd: (action) => `Zaregistruj sa a ${action}`,
    period2d: "2 dni", periodWeek: "týždeň", periodMonth: "mesiac",
    descStart: "Vyskúšaj CELÝ PRO obsah na 2 dni — len €1.99. Kalkulačky, orbital, potrubár, komunita, AI. Jednorazovo.",
    descActive: "Aktivuj zváračský arzenál na týždeň. Jednorazová platba, bez záväzku.",
    descPro: "Plný prístup bez limitov — pre profesionálov na 30 dní. Jednorazová platba, bez záväzku.",
    featStartTig: "Manual TIG (DN ≤ 85, stena ≤ 2 mm)",
    featStartCom: "Otvorená komunita (čítanie + chat)",
    featStartAI: "2 AI otázky / deň",
    featStartNoOrb: "Orbitálna kalkulačka",
    featStartNoPip: "Potrubárske tabuľky (plné)",
    featOrbital: "Orbitálna kalkulačka (Axxair / Orbitec / Orbitalum)",
    featTigUnl: "Manual TIG bez obmedzenia",
    featPipefull: "Potrubárske tabuľky (plné)",
    featChat: "Komunitný chat (všetky kanály)",
    featAIUnl: "5 správ denne", featAIPer: " AI poradca + balíčky",
    featAILimit: "3 AI správy / deň",
    featSupport: "Prioritná podpora",
    featBestValue: "Najvýhodnejšie",
    goldExclusive: "ELITE PREMIUM",
    oneYear: "1 ROK", perYear: "/ rok", periodYear: "rok",
    descGold: "Celý PRO arzenál na 1 rok. Exkluzívny zlatý odznak vedľa tvojho mena v komunite — vidia ho všetci. Prémiová zlatá faktúra. Jednorazovo, žiadne automatické obnovenie.",
    featGoldEverything: "Všetko z PRO bez limitov",
    featGoldYear: "Platí 365 dní",
    featGoldBadge: "Exkluzívny GOLD odznak v komunite",
    featGoldInvoice: "Prémiová zlatá faktúra",
    featGoldSaves: "Ušetríš ~€54 vs mesačné PRO",
    actActivateYear: "aktivuj GOLD na 1 rok",
    goldLockTitle: "GOLD sa musí zaslúžiť",
    goldLockSub: "Najprv 20 dní v aktívnom PRO. Až potom môžeš povýšiť na GOLD. Filtrujeme bohatých amatérov — chceme tých, čo to myslia vážne.",
    goldLockProgress: (d, total) => `Máš ${d}/${total} dní v PRO`,
    goldLockNeedPro: "Najprv aktivuj PRO predplatné",
    goldLockFromPaid: "Si o krok od elity. Upgrade na PRO — po 20 dňoch ti GOLD odomkneme automaticky.",
    goldLockAnon: "GOLD nie je pre nováčikov. Najprv 20 dní aktívne v PRO, až potom upgrade. Začni registráciou na PRO.",
    goldAnonBtn: "Zaregistruj sa na PRO — €6,99/mes",
    goldLockButton: "🔒 Uzamknuté",
    testBannerTitle: "TESTOVACIA platobná brána",
    testBannerSub: <>Pre vyskúšanie zadajte testovaciu kartu <span className="font-mono text-zinc-300">4242 4242 4242 4242</span>, dátum v budúcnosti, ľubovoľné CVV. Žiadne reálne peniaze sa neprenesú.</>,
    secureTitle: "Bezpečná platba cez Stripe",
    secureBody: "Platby spracováva Stripe — globálny lídr v online platbách (PCI-DSS Level 1). Karta sa neukladá v našej databáze. Podporujeme Visa, Mastercard, American Express, Apple Pay, Google Pay.",
    legalNote1: "Poskytovateľ: SauberEko s. r. o. · Ivana Krasku 25/9, 972 71 Nováky · IČO 57356050 · IČ DPH SK2122719995 · Ceny sú uvedené vrátane 23 % DPH.",
    legalNote2: "Všetky plány (START, ACTIVE, PRO, GOLD) sú jednorazové platby — po uplynutí platnosti sa nič automaticky neúčtuje. Predĺženie si aktivuješ kedykoľvek v Profile alebo cez pripomienkový email 3 dni pred vypršaním.",
    legalNote3: "Odstúpenie od zmluvy: napíšte na info@saubereko.eu do 14 dní (z. č. 102/2014 Z.z.).",
    moneyBack: "✓ 14-dňová záruka vrátenia peňazí — bez otázok. Nespokojný? Klik v Profile a peniaze späť do 5–10 dní.",
    startBadge: "🎁 Vyskúšaj celý PRO obsah",
  },
  en: {
    twoDays: "2 DAYS", perTwoDays: "/ 2 days",
    autoRenew: "AUTO-RENEW", oneTime: "ONE-TIME",
    paid: "Active subscription", currentPlan: "Your current plan", upgradeTo: "Upgrade to",
    downgradeNote: "Your current plan is higher", mostPopular: "Most popular",
    actSubscribe: (period) => `subscribe for ${period}`,
    actActivate: (period) => `activate for ${period}`,
    regAnd: (action) => `Sign up and ${action}`,
    period2d: "2 days", periodWeek: "a week", periodMonth: "a month",
    descStart: "Try the FULL PRO experience for 2 days — just €1.99. Calculators, orbital, pipefitter, community, AI. One-time.",
    descActive: "Activate the welding arsenal for a week. One-time payment, no commitment.",
    descPro: "Full access without limits — for professionals, 30 days. One-time payment, no commitment.",
    featStartTig: "Manual TIG (DN ≤ 85, wall ≤ 2 mm)",
    featStartCom: "Open community (read + chat)",
    featStartAI: "2 AI questions / day",
    featStartNoOrb: "Orbital calculator",
    featStartNoPip: "Pipefitter tables (full)",
    featOrbital: "Orbital calculator (Axxair / Orbitec / Orbitalum)",
    featTigUnl: "Manual TIG without limits",
    featPipefull: "Pipefitter tables (full)",
    featChat: "Community chat (all channels)",
    featAIUnl: "5 messages/day", featAIPer: " AI advisor + packs",
    featAILimit: "3 AI messages / day",
    featSupport: "Priority support",
    featBestValue: "Best value",
    goldExclusive: "ELITE PREMIUM",
    oneYear: "1 YEAR", perYear: "/ year", periodYear: "year",
    descGold: "The full PRO arsenal for 1 year. Exclusive gold badge next to your name in the community — everyone sees it. Premium gold invoice. One-time payment, no auto-renewal.",
    featGoldEverything: "Everything in PRO, no limits",
    featGoldYear: "Valid 365 days",
    featGoldBadge: "Exclusive GOLD badge in community",
    featGoldInvoice: "Premium gold invoice",
    featGoldSaves: "Save ~€54 vs monthly PRO",
    actActivateYear: "activate GOLD for 1 year",
    goldLockTitle: "GOLD must be earned",
    goldLockSub: "First 20 days on active PRO. Then you can upgrade to GOLD. We filter out wealthy amateurs — we want those who are serious.",
    goldLockProgress: (d, total) => `You're at ${d}/${total} days on PRO`,
    goldLockNeedPro: "Activate PRO subscription first",
    goldLockFromPaid: "You're one step from the elite. Upgrade to PRO — after 20 days, GOLD unlocks automatically.",
    goldLockAnon: "GOLD isn't for newcomers. First 20 days active on PRO, then upgrade. Start by registering for PRO.",
    goldAnonBtn: "Register for PRO — €6.99/mo",
    goldLockButton: "🔒 Locked",
    testBannerTitle: "TEST payment gateway",
    testBannerSub: <>To try it, use the test card <span className="font-mono text-zinc-300">4242 4242 4242 4242</span>, any future date, any CVV. No real money will be charged.</>,
    secureTitle: "Secure payment via Stripe",
    secureBody: "Payments are processed by Stripe — global leader in online payments (PCI-DSS Level 1). Your card is never stored in our database. We support Visa, Mastercard, American Express, Apple Pay, Google Pay.",
    legalNote1: "Provider: SauberEko s. r. o. · Ivana Krasku 25/9, 972 71 Nováky · ID 57356050 · VAT SK2122719995 · Prices are shown including 23 % VAT.",
    legalNote2: "All plans (START, ACTIVE, PRO, GOLD) are one-time payments — nothing is charged automatically once your access period expires. Renew any time in your Profile or via the reminder email sent 3 days before expiry.",
    legalNote3: "Right of withdrawal: email info@saubereko.eu within 14 days (Slovak Act 102/2014).",
    moneyBack: "✓ 14-day money-back guarantee — no questions asked. Not happy? One click in your Profile and the money is back in 5–10 days.",
    startBadge: "🎁 Try the full PRO experience",
  },
  cs: {
    twoDays: "2 DNY", perTwoDays: "/ 2 dny",
    autoRenew: "AUTO-OBNOVA", oneTime: "JEDNORÁZOVĚ",
    paid: "Aktivní předplatné", currentPlan: "Tvůj aktuální plán", upgradeTo: "Upgrade na",
    downgradeNote: "Tvůj aktuální plán je vyšší", mostPopular: "Nejoblíbenější",
    actSubscribe: (period) => `předplať si ${period}`,
    actActivate: (period) => `aktivuj na ${period}`,
    regAnd: (action) => `Zaregistruj se a ${action}`,
    period2d: "2 dny", periodWeek: "týden", periodMonth: "měsíc",
    descStart: "Vyzkoušej CELÝ PRO obsah na 2 dny — jen €1,99. Kalkulačky, orbital, potrubář, komunita, AI. Jednorázově.",
    descActive: "Aktivuj svářečský arzenál na týden. Jednorázová platba, bez závazku.",
    descPro: "Plný přístup bez limitů — pro profesionály na 30 dní. Jednorázová platba, bez závazku.",
    featStartTig: "Manual TIG (DN ≤ 85, stěna ≤ 2 mm)",
    featStartCom: "Otevřená komunita (čtení + chat)",
    featStartAI: "2 AI otázky / den",
    featStartNoOrb: "Orbitální kalkulačka",
    featStartNoPip: "Potrubářské tabulky (plné)",
    featOrbital: "Orbitální kalkulačka (Axxair / Orbitec / Orbitalum)",
    featTigUnl: "Manual TIG bez omezení",
    featPipefull: "Potrubářské tabulky (plné)",
    featChat: "Komunitní chat (všechny kanály)",
    featAIUnl: "5 zpráv denně", featAIPer: " AI poradce + balíčky",
    featAILimit: "3 AI zprávy / den",
    featSupport: "Prioritní podpora",
    featBestValue: "Nejvýhodnější",
    goldExclusive: "ELITE PREMIUM",
    oneYear: "1 ROK", perYear: "/ rok", periodYear: "rok",
    descGold: "Celý PRO arzenál na 1 rok. Exkluzivní zlatý odznak vedle tvého jména v komunitě — vidí ho všichni. Prémiová zlatá faktura. Jednorázově, žádné automatické obnovení.",
    featGoldEverything: "Vše z PRO bez limitů",
    featGoldYear: "Platí 365 dní",
    featGoldBadge: "Exkluzivní GOLD odznak v komunitě",
    featGoldInvoice: "Prémiová zlatá faktura",
    featGoldSaves: "Ušetříš ~€54 vs měsíční PRO",
    actActivateYear: "aktivuj GOLD na 1 rok",
    goldLockTitle: "GOLD se musí zasloužit",
    goldLockSub: "Nejdřív 20 dní v aktivním PRO. Až potom můžeš upgradovat na GOLD. Filtrujeme bohaté amatéry — chceme ty, kdo to myslí vážně.",
    goldLockProgress: (d, total) => `Máš ${d}/${total} dní v PRO`,
    goldLockNeedPro: "Nejdřív aktivuj PRO předplatné",
    goldLockFromPaid: "Jsi krok od elity. Upgraduj na PRO — po 20 dnech ti GOLD odemkneme automaticky.",
    goldLockAnon: "GOLD není pro nováčky. Nejdřív 20 dní aktivně v PRO, až pak upgrade. Začni registrací na PRO.",
    goldAnonBtn: "Zaregistruj se na PRO — €6,99/měs",
    goldLockButton: "🔒 Uzamčeno",
    testBannerTitle: "TESTOVACÍ platební brána",
    testBannerSub: <>Pro vyzkoušení zadejte testovací kartu <span className="font-mono text-zinc-300">4242 4242 4242 4242</span>, datum v budoucnosti, libovolné CVV. Žádné reálné peníze se nepřevedou.</>,
    secureTitle: "Bezpečná platba přes Stripe",
    secureBody: "Platby zpracovává Stripe — globální lídr v online platbách (PCI-DSS Level 1). Karta se neukládá v naší databázi. Podporujeme Visa, Mastercard, American Express, Apple Pay, Google Pay.",
    legalNote1: "Poskytovatel: SauberEko s. r. o. · Ivana Krasku 25/9, 972 71 Nováky · IČO 57356050 · IČ DPH SK2122719995 · Ceny jsou uvedeny včetně 23 % DPH.",
    legalNote2: "Všechny plány (START, ACTIVE, PRO, GOLD) jsou jednorázové platby — po uplynutí platnosti se nic automaticky neúčtuje. Prodloužení si aktivuješ kdykoli v Profilu nebo přes připomínkový email 3 dny před vypršením.",
    legalNote3: "Odstoupení od smlouvy: napiš na info@saubereko.eu do 14 dní (z. č. 102/2014 Z.z.).",
    moneyBack: "✓ 14-denní záruka vrácení peněz — bez otázek. Nespokojen? Klik v Profilu a peníze zpět do 5–10 dnů.",
    startBadge: "🎁 Vyzkoušej celý PRO obsah",
  },
  de: {
    twoDays: "2 TAGE", perTwoDays: "/ 2 Tage",
    autoRenew: "AUTO-VERLÄNGERUNG", oneTime: "EINMALIG",
    paid: "Aktives Abo", currentPlan: "Dein aktueller Plan", upgradeTo: "Upgrade auf",
    downgradeNote: "Dein aktueller Plan ist höher", mostPopular: "Beliebteste",
    actSubscribe: (period) => `Abo für ${period} starten`,
    actActivate: (period) => `für ${period} aktivieren`,
    regAnd: (action) => `Registrieren und ${action}`,
    period2d: "2 Tage", periodWeek: "eine Woche", periodMonth: "einen Monat",
    descStart: "Teste den KOMPLETTEN PRO-Zugang 2 Tage — nur €1,99. Rechner, Orbital, Rohrleger, Community, KI. Einmalig.",
    descActive: "Aktiviere das Schweißarsenal für eine Woche. Einmalige Zahlung, keine Verpflichtung.",
    descPro: "Voller Zugang ohne Limits — für Profis, 30 Tage. Einmalige Zahlung, keine Verpflichtung.",
    featStartTig: "Manuelles WIG (DN ≤ 85, Wand ≤ 2 mm)",
    featStartCom: "Offene Community (Lesen + Chat)",
    featStartAI: "2 KI-Fragen / Tag",
    featStartNoOrb: "Orbital-Rechner",
    featStartNoPip: "Rohrleger-Tabellen (vollständig)",
    featOrbital: "Orbital-Rechner (Axxair / Orbitec / Orbitalum)",
    featTigUnl: "Manuelles WIG ohne Limits",
    featPipefull: "Rohrleger-Tabellen (vollständig)",
    featChat: "Community-Chat (alle Kanäle)",
    featAIUnl: "5 Nachrichten/Tag", featAIPer: " KI-Berater + Pakete",
    featAILimit: "3 KI-Nachrichten / Tag",
    featSupport: "Prioritäts-Support",
    featBestValue: "Bestes Angebot",
    goldExclusive: "ELITE PREMIUM",
    oneYear: "1 JAHR", perYear: "/ Jahr", periodYear: "ein Jahr",
    descGold: "Das gesamte PRO-Arsenal für 1 Jahr. Exklusives Gold-Abzeichen neben deinem Namen in der Community — jeder sieht es. Premium-Goldrechnung. Einmalige Zahlung, keine automatische Verlängerung.",
    featGoldEverything: "Alles aus PRO, ohne Limits",
    featGoldYear: "Gültig 365 Tage",
    featGoldBadge: "Exklusives GOLD-Abzeichen in der Community",
    featGoldInvoice: "Premium-Goldrechnung",
    featGoldSaves: "Spare ~€54 vs monatliches PRO",
    actActivateYear: "GOLD für 1 Jahr aktivieren",
    goldLockTitle: "GOLD muss verdient werden",
    goldLockSub: "Zuerst 20 Tage im aktiven PRO. Erst dann kannst du auf GOLD upgraden. Wir filtern reiche Amateure heraus — wir wollen die, die es ernst meinen.",
    goldLockProgress: (d, total) => `Du hast ${d}/${total} Tage im PRO`,
    goldLockNeedPro: "Zuerst PRO-Abo aktivieren",
    goldLockFromPaid: "Du bist einen Schritt von der Elite entfernt. Upgrade auf PRO — nach 20 Tagen schalten wir GOLD automatisch frei.",
    goldLockAnon: "GOLD ist nichts für Neulinge. Zuerst 20 Tage aktiv im PRO, dann upgraden. Beginne mit der PRO-Registrierung.",
    goldAnonBtn: "Für PRO registrieren — €6,99/Mon",
    goldLockButton: "🔒 Gesperrt",
    testBannerTitle: "TEST-Zahlungs-Gateway",
    testBannerSub: <>Zum Testen verwenden Sie die Testkarte <span className="font-mono text-zinc-300">4242 4242 4242 4242</span>, beliebiges zukünftiges Datum, beliebige CVV. Es wird kein echtes Geld abgebucht.</>,
    secureTitle: "Sichere Zahlung über Stripe",
    secureBody: "Zahlungen werden von Stripe abgewickelt — globaler Marktführer für Online-Zahlungen (PCI-DSS Level 1). Die Karte wird in unserer Datenbank nicht gespeichert. Wir unterstützen Visa, Mastercard, American Express, Apple Pay, Google Pay.",
    legalNote1: "Anbieter: SauberEko s. r. o. · Ivana Krasku 25/9, 972 71 Nováky · ID-Nr. 57356050 · USt-IdNr. SK2122719995 · Alle Preise inkl. 23 % MwSt.",
    legalNote2: "Alle Pläne (START, ACTIVE, PRO, GOLD) sind Einmalzahlungen — nach Ablauf wird nichts automatisch abgebucht. Verlängern jederzeit im Profil oder über die Erinnerungs-E-Mail 3 Tage vor Ablauf.",
    legalNote3: "Widerrufsrecht: schreiben Sie an info@saubereko.eu innerhalb von 14 Tagen (slowakisches Gesetz 102/2014).",
    moneyBack: "✓ 14 Tage Geld-zurück-Garantie — keine Fragen. Unzufrieden? Ein Klick im Profil und das Geld ist in 5–10 Tagen zurück.",
    startBadge: "🎁 Vollen PRO-Zugang testen",
  },
  pl: {
    twoDays: "2 DNI", perTwoDays: "/ 2 dni",
    autoRenew: "AUTO-ODNAWIANIE", oneTime: "JEDNORAZOWO",
    paid: "Aktywna subskrypcja", currentPlan: "Twój aktualny plan", upgradeTo: "Upgrade do",
    downgradeNote: "Twój aktualny plan jest wyższy", mostPopular: "Najpopularniejsze",
    actSubscribe: (period) => `subskrybuj na ${period}`,
    actActivate: (period) => `aktywuj na ${period}`,
    regAnd: (action) => `Zarejestruj się i ${action}`,
    period2d: "2 dni", periodWeek: "tydzień", periodMonth: "miesiąc",
    descStart: "Wypróbuj CAŁY pakiet PRO na 2 dni — tylko €1,99. Kalkulatory, orbital, rurociągi, społeczność, AI. Jednorazowo.",
    descActive: "Aktywuj arsenał spawalniczy na tydzień. Płatność jednorazowa, bez zobowiązań.",
    descPro: "Pełen dostęp bez limitów — dla profesjonalistów na 30 dni. Płatność jednorazowa, bez zobowiązań.",
    featStartTig: "Manual TIG (DN ≤ 85, ścianka ≤ 2 mm)",
    featStartCom: "Otwarta społeczność (czytanie + czat)",
    featStartAI: "2 pytania AI / dzień",
    featStartNoOrb: "Kalkulator orbitalny",
    featStartNoPip: "Tabele rurociągowe (pełne)",
    featOrbital: "Kalkulator orbitalny (Axxair / Orbitec / Orbitalum)",
    featTigUnl: "Manual TIG bez ograniczeń",
    featPipefull: "Tabele rurociągowe (pełne)",
    featChat: "Czat społeczności (wszystkie kanały)",
    featAIUnl: "5 wiadomości dziennie", featAIPer: " doradca AI + pakiety",
    featAILimit: "3 wiadomości AI / dzień",
    featSupport: "Priorytetowe wsparcie",
    featBestValue: "Najlepsza oferta",
    goldExclusive: "ELITE PREMIUM",
    oneYear: "1 ROK", perYear: "/ rok", periodYear: "rok",
    descGold: "Cały arsenał PRO na 1 rok. Ekskluzywna złota odznaka obok twojego imienia w społeczności — wszyscy ją widzą. Premium złota faktura. Jednorazowa płatność, bez automatycznego odnowienia.",
    featGoldEverything: "Wszystko z PRO bez limitów",
    featGoldYear: "Ważne 365 dni",
    featGoldBadge: "Ekskluzywna odznaka GOLD w społeczności",
    featGoldInvoice: "Premium złota faktura",
    featGoldSaves: "Oszczędź ~€54 vs miesięczne PRO",
    actActivateYear: "aktywuj GOLD na 1 rok",
    goldLockTitle: "GOLD trzeba zasłużyć",
    goldLockSub: "Najpierw 20 dni w aktywnym PRO. Dopiero potem możesz przejść na GOLD. Filtrujemy bogatych amatorów — chcemy tych, którzy traktują to poważnie.",
    goldLockProgress: (d, total) => `Masz ${d}/${total} dni w PRO`,
    goldLockNeedPro: "Najpierw aktywuj subskrypcję PRO",
    goldLockFromPaid: "Jesteś krok od elity. Przejdź na PRO — po 20 dniach odblokujemy GOLD automatycznie.",
    goldLockAnon: "GOLD nie jest dla nowicjuszy. Najpierw 20 dni aktywnie w PRO, dopiero potem upgrade. Zacznij od rejestracji na PRO.",
    goldAnonBtn: "Zarejestruj się na PRO — €6,99/mies",
    goldLockButton: "🔒 Zablokowane",
    testBannerTitle: "TESTOWA bramka płatności",
    testBannerSub: <>Aby przetestować, wpisz kartę testową <span className="font-mono text-zinc-300">4242 4242 4242 4242</span>, dowolną datę w przyszłości, dowolne CVV. Żadne realne pieniądze nie zostaną pobrane.</>,
    secureTitle: "Bezpieczna płatność przez Stripe",
    secureBody: "Płatności obsługuje Stripe — globalny lider w płatnościach online (PCI-DSS Level 1). Karta nie jest zapisywana w naszej bazie. Wspieramy Visa, Mastercard, American Express, Apple Pay, Google Pay.",
    legalNote1: "Dostawca: SauberEko s. r. o. · Ivana Krasku 25/9, 972 71 Nováky · REGON 57356050 · NIP SK2122719995 · Ceny podane z 23 % VAT.",
    legalNote2: "Wszystkie plany (START, ACTIVE, PRO, GOLD) to płatności jednorazowe — po wygaśnięciu dostępu nic nie jest naliczane automatycznie. Odnów w każdej chwili w Profilu lub przez email przypominający wysłany 3 dni przed wygaśnięciem.",
    legalNote3: "Odstąpienie od umowy: napisz na info@saubereko.eu w ciągu 14 dni (słowacka ustawa 102/2014).",
    moneyBack: "✓ 14-dniowa gwarancja zwrotu pieniędzy — bez pytań. Niezadowolony? Jeden klik w Profilu i pieniądze wracają w 5–10 dni.",
    startBadge: "🎁 Wypróbuj cały pakiet PRO",
  },
  fr: {
    twoDays: "2 JOURS", perTwoDays: "/ 2 jours",
    autoRenew: "AUTO-RENOUVELLEMENT", oneTime: "UNIQUE",
    paid: "Abonnement actif", currentPlan: "Ton plan actuel", upgradeTo: "Upgrade vers",
    downgradeNote: "Ton plan actuel est supérieur", mostPopular: "Le plus populaire",
    actSubscribe: (period) => `abonne-toi pour ${period}`, // recurring
    actActivate: (period) => `active pour ${period}`,   // one-time
    regAnd: (action) => `Inscris-toi et ${action}`,
    period2d: "2 jours", periodWeek: "semaine", periodMonth: "mois",
    descStart: "Teste TOUT le contenu PRO pendant 2 jours — seulement 1,99 €. Calculateurs, orbital, tuyauteur, communauté, IA. Paiement unique.",
    descActive: "Active l'arsenal du soudeur pour une semaine. Paiement unique, sans engagement.",
    descPro: "Accès complet sans limites — pour les pros pendant 30 jours. Paiement unique, sans engagement.",
    featStartTig: "TIG manuel (DN ≤ 85, épaisseur ≤ 2 mm)",
    featStartCom: "Communauté ouverte (lecture + chat)",
    featStartAI: "2 questions IA / jour",
    featStartNoOrb: "Calculateur orbital",
    featStartNoPip: "Tableaux tuyauterie (complets)",
    featOrbital: "Calculateur orbital (Axxair / Orbitec / Orbitalum)",
    featTigUnl: "TIG manuel sans restriction",
    featPipefull: "Tableaux tuyauterie (complets)",
    featChat: "Chat communautaire (tous les canaux)",
    featAIUnl: "5 messages/jour", featAIPer: " conseiller IA + packs",
    featAILimit: "3 messages IA / jour",
    featSupport: "Support prioritaire",
    featBestValue: "Meilleur rapport qualité-prix",
    goldExclusive: "ELITE PREMIUM",
    oneYear: "1 AN", perYear: "/ an", periodYear: "an",
    descGold: "Tout l'arsenal PRO pendant 1 an. Badge doré exclusif à côté de ton nom dans la communauté — tout le monde le voit. Facture dorée premium. Paiement unique, pas de renouvellement automatique.",
    featGoldEverything: "Tout ce qui est dans PRO sans limites",
    featGoldYear: "Valable 365 jours",
    featGoldBadge: "Badge GOLD exclusif dans la communauté",
    featGoldInvoice: "Facture dorée premium",
    featGoldSaves: "Économise ~54 € vs PRO mensuel",
    actActivateYear: "active GOLD pour 1 an",
    goldLockTitle: "GOLD se mérite",
    goldLockSub: "D'abord 20 jours en PRO actif. Ensuite seulement tu peux passer à GOLD. On filtre les amateurs fortunés — on veut ceux qui sont sérieux.",
    goldLockProgress: (d, total) => `Tu as ${d}/${total} jours en PRO`,
    goldLockNeedPro: "D'abord active l'abonnement PRO",
    goldLockFromPaid: "Tu es à un pas de l'élite. Upgrade vers PRO — après 20 jours on te débloque GOLD automatiquement.",
    goldLockAnon: "GOLD n'est pas pour les débutants. D'abord 20 jours actifs en PRO, ensuite upgrade. Commence par l'inscription à PRO.",
    goldAnonBtn: "Inscris-toi à PRO — 6,99 €/mois",
    goldLockButton: "🔒 Verrouillé",
    testBannerTitle: "Passerelle de paiement TEST",
    testBannerSub: <>Pour tester, entre la carte test <span className="font-mono text-zinc-300">4242 4242 4242 4242</span>, une date future, n'importe quel CVV. Aucun argent réel ne sera transféré.</>,
    secureTitle: "Paiement sécurisé via Stripe",
    secureBody: "Les paiements sont traités par Stripe — leader mondial des paiements en ligne (PCI-DSS Level 1). La carte n'est pas stockée dans notre base de données. Nous acceptons Visa, Mastercard, American Express, Apple Pay, Google Pay.",
    legalNote1: "Fournisseur : SauberEko s. r. o. · Ivana Krasku 25/9, 972 71 Nováky · IČO 57356050 · IČ DPH SK2122719995 · Les prix sont TTC 23 % TVA.",
    legalNote2: "Tous les plans (START, ACTIVE, PRO, GOLD) sont des paiements uniques — rien n'est débité automatiquement après expiration. Tu actives le renouvellement quand tu veux dans ton Profil ou via l'email de rappel 3 jours avant expiration.",
    legalNote3: "Droit de rétractation : écris à info@saubereko.eu sous 14 jours (loi n° 102/2014 Rec.).",
    moneyBack: "✓ Garantie satisfait ou remboursé 14 jours — sans question. Pas content ? Clic dans le Profil et argent remboursé sous 5–10 jours.",
    startBadge: "🎁 Teste tout le contenu PRO",
},
};

// Plan rank: higher = better tier. Used for upgrade/downgrade comparison.
// Backend plan_tier values: "free" | "start" | "active" | "pro" | "gold" | "admin".
// Backend pricing endpoint plan IDs:  "start" | "weekly" | "monthly" | "gold".
const TIER_RANK = { free: 0, start: 1, active: 2, weekly: 2, pro: 3, monthly: 3, gold: 4, admin: 99 };
const tierRank = (key) => TIER_RANK[String(key || "").toLowerCase()] ?? 0;

export default function Pricing() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const tr = TR[lang] || TR.sk;
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [mode, setMode] = useState(null);
  const [goldElig, setGoldElig] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((r) => setPlans(r.data));
    api.get("/stripe/mode").then((r) => setMode(r.data.mode)).catch(() => {});
    // GOLD eligibility — only fetch when logged in. Anonymous users see the card unlocked.
    if (user) {
      api.get("/billing/gold/eligibility").then((r) => setGoldElig(r.data)).catch(() => {});
    } else {
      setGoldElig(null);
    }
  }, [user]);

  // Compare the user's current plan tier against this card's plan id.
  // Returns: "current" | "upgrade" | "lower" | null  (null = not logged in / FREE)
  const planRelation = (planId) => {
    if (!user || !user.is_subscribed) return null;
    const userRank = tierRank(user.plan_tier);
    const planRankN = tierRank(planId);
    if (planRankN === userRank) return "current";
    if (planRankN > userRank) return "upgrade";
    return "lower";
  };

  const buttonLabel = (p) => {
    const rel = planRelation(p.id);
    const period = p.id === "start" ? tr.period2d : p.id === "weekly" ? tr.periodWeek : p.id === "gold" ? tr.periodYear : tr.periodMonth;
    const amount = `€${p.amount.toFixed(2)}`;
    const isRecurring = p.billing === "recurring";
    const action = p.id === "gold" ? tr.actActivateYear : (isRecurring ? tr.actSubscribe(period) : tr.actActivate(period));
    const planLabel = p.id === "monthly" ? "PRO" : p.id === "weekly" ? "ACTIVE" : p.id === "gold" ? "GOLD" : "START";

    if (rel === "current") return tr.currentPlan;
    if (rel === "lower") return tr.downgradeNote;
    if (rel === "upgrade") return `${tr.upgradeTo} ${planLabel} — ${amount}`;
    if (user) return `${action.charAt(0).toUpperCase() + action.slice(1)} — ${amount}`;
    return `${tr.regAnd(action)} — ${amount}`;
  };

  if (authLoading) return null;

  const subscribe = async (plan) => {
    if (!user) {
      nav(`/register?next=/pricing&plan=${plan}`);
      return;
    }
    setBusy(plan);
    try {
      const r = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        plan,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setBusy(null);
      const detail = e.response?.data?.detail || "Checkout error";
      if (typeof detail === "string" && detail.startsWith("BILLING_REQUIRED")) {
        nav(`/profile/billing?first=1&next=${encodeURIComponent("/pricing")}`);
        return;
      }
      // Graceful Stripe degradation — show friendly message without exposing tech details
      if (typeof detail === "string" && (detail.startsWith("STRIPE_UNAVAILABLE") || detail.startsWith("CARD_ERROR"))) {
        const clean = detail.split(":").slice(1).join(":").trim() || detail;
        alert(clean);
        return;
      }
      // GOLD locked — re-fetch eligibility so the lock card shows + show explanatory message
      if (typeof detail === "string" && detail.startsWith("GOLD_LOCKED")) {
        api.get("/billing/gold/eligibility").then((r) => setGoldElig(r.data)).catch(() => {});
        alert(tr.goldLockTitle + " — " + tr.goldLockSub);
        return;
      }
      alert(detail);
    }
  };

  return (
    <div data-testid="pricing-page" className="max-w-6xl mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <span className="wb-tag !border-gold-600 !text-gold-500">PROGRAM</span>
        <h1 className="font-display font-black text-5xl md:text-6xl mt-3">{t.pricing.title}</h1>
        <p className="text-zinc-400 mt-3 max-w-xl mx-auto">{t.pricing.sub}</p>
      </div>

      {mode === "test" && (
        <div data-testid="stripe-test-banner" className="max-w-3xl mx-auto mb-6 border-l-2 border-amber-500 bg-amber-500/5 p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-display font-bold text-amber-400">{tr.testBannerTitle}</div>
            <div className="text-zinc-400 text-xs mt-1">{tr.testBannerSub}</div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto mb-10">
        <LaunchPromoBanner />
      </div>

      {/* SauberEko® certificate holders — free PRO access */}
      <div className="max-w-5xl mx-auto">
        <CertHolderBanner />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800">
        {plans.map((p) => {
          const isPopular = p.id === "monthly";
          const isStart = p.id === "start";
          const isGold = p.id === "gold";
          return (
          <div
            key={p.id}
            data-testid={`plan-${p.id}`}
            className={
              isGold
                ? "bg-gradient-to-br from-amber-950/60 via-zinc-950 to-zinc-950 p-8 relative border-2 border-amber-400 shadow-[0_0_60px_-20px_rgba(251,191,36,0.7)] z-30 mt-4 md:mt-0"
                : `bg-[var(--wb-surface)] p-8 relative ${isPopular ? "border-gold-500 border-2 md:scale-[1.03] md:-my-2 shadow-[0_0_40px_-15px_rgba(212,175,55,0.6)] z-10" : isStart ? "border-zinc-700 border-2" : "border-gold-600/60 border-2"}`
            }
          >
            {isPopular && (
              <div
                data-testid="popular-badge"
                className="absolute -top-3 right-6 inline-flex items-center gap-1 px-3 py-1 bg-gold-500 text-black font-display font-black text-[10px] uppercase tracking-widest shadow-lg"
              >
                <Zap className="w-3 h-3" /> {tr.mostPopular}
              </div>
            )}
            {isStart && (
              <div
                data-testid="start-badge"
                className="absolute -top-3 right-6 inline-flex items-center gap-1 px-3 py-1 bg-emerald-500 text-black font-display font-black text-[10px] uppercase tracking-widest shadow-[0_0_18px_-4px_rgba(16,185,129,0.9)] whitespace-nowrap"
              >
                {tr.startBadge}
              </div>
            )}
            {isGold && (
              <div
                data-testid="gold-badge"
                className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black font-display font-black text-[10px] uppercase tracking-widest shadow-[0_0_20px_-2px_rgba(251,191,36,0.8)] whitespace-nowrap"
              >
                <Crown className="w-3 h-3" /> {tr.goldExclusive}
              </div>
            )}
            <div className={`absolute -top-3 left-6 wb-tag !bg-[var(--wb-bg)] ${isGold ? "!border-amber-400 !text-amber-300" : "!border-gold-600 !text-gold-500"} px-2 py-1`}>
              {isGold ? <Crown className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
              {p.id === "gold" ? "GOLD" : p.id === "monthly" ? "PRO" : p.id === "start" ? "START" : "ACTIVE"}
            </div>
            <div className="flex items-center gap-2 mb-3">
              <div className={`wb-tag ${isGold ? "!text-amber-300 !border-amber-700" : ""}`}>
                {p.id === "monthly" ? t.pricing.monthly : p.id === "start" ? tr.twoDays : p.id === "gold" ? tr.oneYear : t.pricing.weekly}
              </div>
              <div className={`wb-tag ${p.billing === "recurring" ? "!text-green-400 !border-green-700" : "!text-zinc-400 !border-zinc-700"}`}>
                {p.billing === "recurring" ? tr.autoRenew : tr.oneTime}
              </div>
            </div>
            <div className={`font-display font-black text-5xl ${isGold ? "bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent" : ""}`}>€{p.amount.toFixed(2)}</div>
            <div className={`text-xs font-mono uppercase ${isGold ? "text-amber-400" : "text-zinc-500"}`}>
              {p.id === "monthly" ? t.pricing.perMonth : p.id === "start" ? tr.perTwoDays : p.id === "gold" ? tr.perYear : t.pricing.perWeek}
            </div>
            <p className="text-sm text-zinc-400 mt-4">
              {p.id === "monthly" ? tr.descPro :
               p.id === "start" ? tr.descStart :
               p.id === "gold" ? tr.descGold :
               tr.descActive}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-zinc-300">
              {p.id === "start" ? (
                <>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featStartTig}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featStartCom}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> <strong>{tr.featStartAI}</strong></li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featStartNoOrb}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featStartNoPip}</li>
                </>
              ) : p.id === "gold" ? (
                <>
                  <li className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> <strong className="text-amber-200">{tr.featGoldEverything}</strong></li>
                  <li className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> {tr.featGoldYear}</li>
                  <li className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> <strong className="text-amber-200">{tr.featGoldBadge}</strong></li>
                  <li className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> {tr.featGoldInvoice}</li>
                  <li className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> <span className="text-amber-300">{tr.featGoldSaves}</span></li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featOrbital}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featTigUnl}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featPipefull}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featChat}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" />
                    {(p.id === "monthly" || p.id === "gold") ? <><strong>{tr.featAIUnl}</strong>{tr.featAIPer}</> : <><strong>{tr.featAILimit}</strong></>}
                  </li>
                  {p.id === "monthly" && (
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featSupport}</li>
                  )}
                  {p.id === "monthly" && (
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold-500" /> {tr.featBestValue}</li>
                  )}
                </>
              )}
            </ul>
            <button
              data-testid={`subscribe-${p.id}`}
              disabled={!!busy || ["current", "lower"].includes(planRelation(p.id)) || (isGold && user && goldElig && !goldElig.eligible)}
              onClick={() => {
                // Anonymous user clicking GOLD → redirect them to PRO registration instead.
                if (isGold && !user) {
                  nav(`/register?next=/pricing&plan=monthly`);
                  return;
                }
                subscribe(p.id);
              }}
              className={
                isGold
                  ? "w-full py-3 mt-8 text-sm disabled:opacity-50 flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 hover:from-amber-300 hover:via-yellow-200 hover:to-amber-400 text-black font-display font-black uppercase tracking-wider shadow-[0_0_30px_-5px_rgba(251,191,36,0.8)]"
                  : "wb-btn-primary w-full py-3 mt-8 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              }
            >
              {busy === p.id && <Loader2 className="w-4 h-4 animate-spin" />}
              {isGold && !user ? (
                <><Crown className="w-4 h-4" /> {tr.goldAnonBtn}</>
              ) : isGold && user && goldElig && !goldElig.eligible ? (
                <><Lock className="w-4 h-4" /> {tr.goldLockButton}</>
              ) : buttonLabel(p)}
            </button>
            {isGold && !user && (
              <div data-testid="gold-anon-notice" className="mt-4 border-l-2 border-amber-500 bg-amber-950/30 p-4">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-amber-200 text-sm">{tr.goldLockTitle}</div>
                    <p className="text-[11px] text-amber-300/80 mt-1 leading-relaxed">{tr.goldLockAnon}</p>
                  </div>
                </div>
              </div>
            )}
            {isGold && user && goldElig && !goldElig.eligible && (
              <div data-testid="gold-lock-card" className="mt-4 border-l-2 border-amber-500 bg-amber-950/30 p-4">
                <div className="flex items-start gap-2 mb-2">
                  <Lock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-display font-bold text-amber-200 text-sm">{tr.goldLockTitle}</div>
                    <p className="text-[11px] text-amber-300/70 mt-1 leading-relaxed">{tr.goldLockSub}</p>
                  </div>
                </div>
                {goldElig.reason === "not_on_pro" ? (
                  <div className="text-xs font-mono text-amber-400 mt-2 pl-6 leading-relaxed">
                    {["active", "start"].includes((goldElig.current_tier || "").toLowerCase())
                      ? <>✨ {tr.goldLockFromPaid}</>
                      : <>→ {tr.goldLockNeedPro}</>}
                  </div>
                ) : (
                  <div className="mt-3 pl-6">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-amber-400 mb-1.5">
                      {tr.goldLockProgress(goldElig.pro_days, goldElig.days_required)}
                    </div>
                    <div className="h-1.5 bg-zinc-800 overflow-hidden">
                      <div
                        data-testid="gold-progress-bar"
                        className="h-full bg-gradient-to-r from-amber-400 to-yellow-300 transition-all duration-500"
                        style={{ width: `${Math.min(100, (goldElig.pro_days / goldElig.days_required) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      <div className="mt-12 max-w-3xl mx-auto border-t border-zinc-800 pt-8 space-y-4">
        <div className="flex items-start gap-3 text-sm text-zinc-400">
          <ShieldCheck className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-white mb-1">{tr.secureTitle}</div>
            <p className="text-xs leading-relaxed">{tr.secureBody}</p>
          </div>
        </div>
        <div data-testid="pricing-money-back" className="border-l-2 border-green-700 bg-green-950/20 p-4 mb-3">
          <p className="text-sm text-green-200 leading-relaxed font-display">{tr.moneyBack}</p>
        </div>
        <div data-testid="pricing-legal-block" className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 leading-relaxed">
          {tr.legalNote1}<br/>
          {tr.legalNote2}<br/>
          <span className="text-zinc-700">{tr.legalNote3}</span>
        </div>
      </div>
    </div>
  );
}
